import { useLanguage } from "@/context/LanguageContext";
import { ArrowRight, Play } from "lucide-react";

export function HeroSection() {
  const { t } = useLanguage();

  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden bg-white pt-20"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-60" />
      
      {/* Accent orb */}
      <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-sky-100/40 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-50/50 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-[#22A7D1] animate-pulse" />
              <span className="text-xs sm:text-sm font-bold tracking-[0.15em] text-[#22A7D1]">
                {t("hero.overline")}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[1.05]">
              {t("hero.title")}
              <br />
              <span className="text-[#22A7D1]">{t("hero.titleHighlight")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-500 max-w-lg">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a
                href="https://bayi.entegrepanel.net/register"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white rounded-full bg-[#22A7D1] hover:bg-[#1B8FB5] transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sky-500/20"
                data-testid="hero-cta-register"
              >
                {t("hero.cta")}
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://bayi.entegrepanel.net/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-slate-700 rounded-full border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-demo"
              >
                <Play className="w-4 h-4" />
                {t("hero.ctaSecondary")}
              </a>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { value: "500+", label: t("stats.dealers") },
                { value: "10K+", label: t("stats.orders") },
                { value: "50K+", label: t("stats.quotes") },
                { value: "99%", label: t("stats.satisfaction") },
              ].map((stat, i) => (
                <div key={i} data-testid={`stat-${i}`}>
                  <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Dashboard Preview + Illustration */}
          <div className="relative">
            {/* Generated illustration behind */}
            <div className="absolute -top-8 -right-8 w-64 h-64 opacity-20 hidden lg:block">
              <img
                src="/images/hero_business.png"
                alt=""
                width="256"
                height="256"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-200/50 bg-white">
              <img
                src="/images/edited_dashboard.png"
                alt={t("imageAlt.heroDashboard")}
                width="1200"
                height="750"
                className="w-full h-auto"
                loading="eager"
                fetchpriority="high"
                decoding="async"
              />
            </div>
            {/* Floating card - conversion rate */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border border-slate-200 shadow-lg p-4 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center">
                  <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">%46.1</div>
                  <div className="text-xs text-slate-500">{t("hero.conversionRate")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
